import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { router } from '@inertiajs/react'
import type { FormDataConvertible } from '@inertiajs/core'
import { ChevronDown, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import type { CronJob, CronJobSchedule } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

type ScheduleKind = 'cron' | 'every' | 'at'
type DeliveryMode = 'none' | 'announce' | 'webhook'

interface Props {
  job: CronJob | null
  open: boolean
  basePath: string
  onOpenChange: (open: boolean) => void
}

interface FormState {
  name: string
  description: string
  enabled: boolean
  deleteAfterRun: boolean
  scheduleKind: ScheduleKind
  cronExpr: string
  timezone: string
  everyMinutes: string
  at: string
  payloadText: string
  deliveryMode: DeliveryMode
  deliveryChannel: string
  deliveryTo: string
  deliveryThreadId: string
  deliveryAccountId: string
  deliveryBestEffort: boolean
}

function scheduleKind(schedule: CronJobSchedule): ScheduleKind {
  if (schedule.kind === 'every') return 'every'
  if (schedule.kind === 'at') return 'at'
  return 'cron'
}

function toDateTimeLocal(value?: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

function fromDateTimeLocal(value: string): string {
  return new Date(value).toISOString()
}

function initialState(job: CronJob | null): FormState {
  const kind = job ? scheduleKind(job.schedule) : 'cron'
  const everyMinutes = job?.schedule.everyMs ? Math.max(1, Math.round(job.schedule.everyMs / 60_000)) : 60
  const delivery = job?.delivery

  return {
    name: job?.name ?? '',
    description: job?.description ?? '',
    enabled: job?.enabled ?? true,
    deleteAfterRun: job?.deleteAfterRun ?? false,
    scheduleKind: kind,
    cronExpr: job?.schedule.expr ?? '0 9 * * *',
    timezone: job?.schedule.tz ?? '',
    everyMinutes: String(everyMinutes),
    at: toDateTimeLocal(job?.schedule.at),
    payloadText: job?.payload.message ?? job?.payload.text ?? '',
    deliveryMode: (delivery?.mode as DeliveryMode | undefined) ?? 'none',
    deliveryChannel: delivery?.channel ?? '',
    deliveryTo: delivery?.to ?? '',
    deliveryThreadId: String(delivery?.thread_ts ?? delivery?.threadId ?? ''),
    deliveryAccountId: delivery?.accountId ?? '',
    deliveryBestEffort: delivery?.bestEffort ?? false,
  }
}

function compactUndefined(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(compactUndefined)
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce<Record<string, unknown>>((memo, [key, child]) => {
      const compacted = compactUndefined(child)
      if (compacted !== undefined) memo[key] = compacted
      return memo
    }, {})
  }
  return value
}

function optional(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function buildSchedule(job: CronJob, form: FormState): CronJobSchedule {
  if (form.scheduleKind === 'every') {
    return compactUndefined({
      kind: 'every',
      everyMs: Math.max(1, Number.parseInt(form.everyMinutes, 10) || 1) * 60_000,
      anchorMs: job.schedule.kind === 'every' ? job.schedule.anchorMs : undefined,
    }) as CronJobSchedule
  }
  if (form.scheduleKind === 'at') {
    return { kind: 'at', at: fromDateTimeLocal(form.at) }
  }
  return compactUndefined({
    kind: 'cron',
    expr: form.cronExpr.trim(),
    tz: optional(form.timezone),
    staggerMs: job.schedule.kind === 'cron' ? job.schedule.staggerMs : undefined,
  }) as CronJobSchedule
}

export default function RotinaEditDialog({ job, open, basePath, onOpenChange }: Props) {
  const [form, setForm] = useState<FormState>(() => initialState(job))
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (job && open) {
      setForm(initialState(job))
      setAdvancedOpen(false)
    }
  }, [job, open])

  if (!job) return null

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(current => ({ ...current, [key]: value }))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const deliveryEnabled = form.deliveryMode !== 'none'
    const payload = (job.payload.kind === 'systemEvent'
      ? compactUndefined({ ...job.payload, kind: 'systemEvent', text: form.payloadText.trim() })
      : compactUndefined({ ...job.payload, kind: 'agentTurn', message: form.payloadText.trim() })) as Record<string, unknown>
    const delivery = compactUndefined({
      ...(job.delivery ?? {}),
      mode: form.deliveryMode,
      channel: deliveryEnabled ? optional(form.deliveryChannel) : undefined,
      to: deliveryEnabled ? optional(form.deliveryTo) : undefined,
      threadId: deliveryEnabled ? form.deliveryThreadId.trim() : undefined,
      accountId: deliveryEnabled ? optional(form.deliveryAccountId) : undefined,
      bestEffort: deliveryEnabled ? form.deliveryBestEffort : undefined,
    }) as Record<string, unknown>

    const cron = compactUndefined({
      name: form.name.trim(),
      description: form.description,
      enabled: form.enabled,
      deleteAfterRun: form.deleteAfterRun,
      schedule: buildSchedule(job, form),
      payload,
      delivery,
    }) as FormDataConvertible

    setProcessing(true)
    router.patch(`${basePath}/crons/${encodeURIComponent(job.id)}`, {
      cron,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
        router.reload({ only: ['crons'] })
      },
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Editar rotina</DialogTitle>
            <DialogDescription>{job.id}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="cron-name">Nome</Label>
              <Input id="cron-name" value={form.name} required onChange={event => setField('name', event.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="cron-description">Descrição</Label>
              <Textarea id="cron-description" value={form.description} onChange={event => setField('description', event.target.value)} />
            </div>
            <label className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="text-sm font-medium text-foreground">Ativa</span>
              <Switch checked={form.enabled} onCheckedChange={checked => setField('enabled', checked)} />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Agendamento</Label>
              <Select value={form.scheduleKind} onValueChange={value => setField('scheduleKind', value as ScheduleKind)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cron">Cron</SelectItem>
                  <SelectItem value="every">Intervalo</SelectItem>
                  <SelectItem value="at">Data única</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.scheduleKind === 'cron' && (
              <>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="cron-expr">Expressão</Label>
                  <Input id="cron-expr" value={form.cronExpr} required onChange={event => setField('cronExpr', event.target.value)} />
                </div>
              </>
            )}
            {form.scheduleKind === 'every' && (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="cron-every">Intervalo em minutos</Label>
                <Input id="cron-every" type="number" min="1" value={form.everyMinutes} required onChange={event => setField('everyMinutes', event.target.value)} />
              </div>
            )}
            {form.scheduleKind === 'at' && (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="cron-at">Data e hora</Label>
                <Input id="cron-at" type="datetime-local" value={form.at} required onChange={event => setField('at', event.target.value)} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cron-payload">Mensagem</Label>
            <Textarea id="cron-payload" value={form.payloadText} required onChange={event => setField('payloadText', event.target.value)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Entrega</Label>
              <Select value={form.deliveryMode} onValueChange={value => setField('deliveryMode', value as DeliveryMode)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem entrega</SelectItem>
                  <SelectItem value="announce">Anunciar em canal</SelectItem>
                  <SelectItem value="webhook">Webhook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.deliveryMode !== 'none' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cron-channel">Canal</Label>
                  <Input id="cron-channel" value={form.deliveryChannel} onChange={event => setField('deliveryChannel', event.target.value)} />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="cron-to">Destino</Label>
                  <Input id="cron-to" value={form.deliveryTo} onChange={event => setField('deliveryTo', event.target.value)} />
                </div>
              </>
            )}
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <div className="rounded-md border border-border">
              <CollapsibleTrigger asChild>
                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-foreground">
                  <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? '' : '-rotate-90'}`} />
                  Avançado
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid gap-4 border-t border-border p-3 sm:grid-cols-2">
                  <label className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                    <span className="text-sm font-medium text-foreground">Excluir após executar</span>
                    <Switch checked={form.deleteAfterRun} onCheckedChange={checked => setField('deleteAfterRun', checked)} />
                  </label>
                  {form.scheduleKind === 'cron' && (
                    <div className="space-y-2">
                      <Label htmlFor="cron-timezone">Fuso horário</Label>
                      <Input id="cron-timezone" value={form.timezone} placeholder="America/Sao_Paulo" onChange={event => setField('timezone', event.target.value)} />
                    </div>
                  )}
                  {form.deliveryMode !== 'none' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="cron-account">Conta</Label>
                        <Input id="cron-account" value={form.deliveryAccountId} onChange={event => setField('deliveryAccountId', event.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cron-thread">Thread</Label>
                        <Input id="cron-thread" value={form.deliveryThreadId} onChange={event => setField('deliveryThreadId', event.target.value)} />
                      </div>
                      <label className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                        <span className="text-sm font-medium text-foreground">Entrega best effort</span>
                        <Switch checked={form.deliveryBestEffort} onCheckedChange={checked => setField('deliveryBestEffort', checked)} />
                      </label>
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
              Cancelar
            </Button>
            <Button type="submit" disabled={processing}>
              <Save className="h-4 w-4" />
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
